import { useAuth, useOrganization } from '@clerk/clerk-react';
import { OrganizationMembershipResource } from '@clerk/types';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Chips, ChipsChangeEvent } from 'primereact/chips';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  endpointFundGET,
  endpointFundGooglWatchChannelDELETE,
  FundGetData,
  GoogleWatchChannel,
  isFundResponseOk,
} from '~/api/def/endpoints/fundEndpoint';
import { endpointInviteUserPOST } from '~/api/def/endpoints/inviteUserEndpoint';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { BasicPage } from '~/components/ui/layout/page/basic/BasicPage';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import MessageComponent from '~/components/ui/misc/MessageComponent';
import { FundsTable } from '~/components/ui/tables/ag/funds/fundsTable';
import { InvitesTable } from '~/components/ui/tables/ag/invites/invitesTable';
import { MembersTable } from '~/components/ui/tables/ag/members/membersTable';
import { GoogleDriveUploader } from '~/components/ui/uploaders/GoogleDriveUploader';
import { canAccess } from '~/utils/rbac';

export const Route = createFileRoute('/_authLayout/settings')({
  beforeLoad: async () => {
    if (!canAccess('setting')) {
      throw notFound();
    }
  },
  component: SettingsComponent,
});

const OrgInvitationsParams = {
  invitations: {
    keepPreviousData: true,
  },
};

const OrgMembersParams = {
  memberships: {
    keepPreviousData: true,
  },
};

type InvitesFormValues = {
  emails: string[];
  role: string;
};

function SettingsComponent() {
  const toast = useRef<Toast>(null);
  const availableRoles = [
    { label: 'Admin', value: 'org:admin' },
    { label: 'Member', value: 'org:member' },
  ];
  const [fundList, setFundList] = useState<FundGetData[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [isPcFolderMapped, setIsPcFolderMapped] = useState<boolean>(false);
  const [inviteUserLoading, setInviteUserLoading] = useState(false);
  const [inviteDialogVisible, setInviteDialogVisible] = useState(false);
  const [gDriveFolderSyncVisible, setGDriveFolderSyncVisible] = useState(false);
  const { orgRole } = useAuth();
  const { isLoaded, invitations, memberships } = useOrganization({
    ...OrgInvitationsParams,
    ...OrgMembersParams,
  });
  const {
    control,
    reset: resetInviteForm,
    trigger,
    handleSubmit: handleInvitesSubmit,
    setValue: setFieldValue,
    formState: { errors },
  } = useForm<InvitesFormValues>({
    defaultValues: {
      emails: [],
      role: '',
    },
  });

  useEffect(() => {
    fetchedFundsList();
  }, []);

  const fetchedFundsList = () => {
    endpointFundGET(true).then((res) => {
      if (isFundResponseOk(res)) {
        setFundList(res.data);
      }
    });
  };

  const handleSendInvite = async () => {
    setInviteDialogVisible(true);
    resetInviteForm();
  };

  const isValidEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const validateEmails = (emails: string[]) => {
    if (!emails || emails.length === 0) {
      return 'At least one email is required.';
    }
    const invalidEmails = [];
    for (const email of emails) {
      if (!isValidEmail(email)) invalidEmails.push(email);
    }
    if (invalidEmails.length)
      return `Invalid emails: ${invalidEmails.join(', ')}`;
    return true;
  };

  const getFullName = (data: OrganizationMembershipResource) => {
    if (data.publicUserData.firstName || data.publicUserData.lastName) {
      return `${data.publicUserData.firstName || ''} ${data.publicUserData.lastName || ''}`.trim();
    }
    return null;
  };

  const showRevokeInviteDialog = (email: string) => {
    confirmDialog({
      message: `Do you want to revoke invite for ${email}?`,
      header: 'Revoke invite',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => handleRevokeInvite(email),
    });
  };

  const handleRevokeInvite = async (email: string) => {
    const invite = invitations?.data?.find(
      (invite) => invite.emailAddress === email,
    );
    try {
      await invite?.revoke();
      await invitations?.revalidate?.();
      toast.current?.show({
        severity: 'success',
        detail: 'Invite revoked successfully.',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        detail: 'Something went wrong while revoking invite',
      });
    }
  };

  const closeInviteDialog = () => {
    if (!inviteDialogVisible) return;
    setInviteDialogVisible(false);
  };

  const onInvitesFormSubmit = async (data: InvitesFormValues) => {
    try {
      setInviteUserLoading(true);
      const res = await endpointInviteUserPOST({
        emails: data.emails,
        role: data.role,
      });
      if (res && res.response === 200) {
        await invitations?.revalidate?.();
        toast.current?.show({
          severity: 'success',
          detail: 'Invitations sent successfully.',
        });
        closeInviteDialog();
      } else {
        throw new Error('Invitation failed');
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        detail: 'Something went wrong while inviting members',
      });
    } finally {
      setInviteUserLoading(false);
    }
  };

  const removeGoogleWatchChannelSync = (channelId: string) => {
    endpointFundGooglWatchChannelDELETE({ channelId: channelId })
      .then((res) => {
        if (res && res.response === 200) {
          toast.current?.show({
            severity: 'success',
            detail: `Google watch channel removed successfully.`,
          });
          fetchedFundsList();
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Form Submitted',
            detail: 'Something went wrong while removing google watch channel.',
          });
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: 'Form Submitted',
          detail: 'Something went wrong while removing google watch channel.',
        });
      });
  };

  return (
    <BasicPage title="Settings">
      <Toast ref={toast} />
      <HeaderContainer>
        <Breadcrumb items={[{ name: 'Settings' }]} />
      </HeaderContainer>

      <PageContent classes={'pb-2'}>
        <Card
          className="mt-4 flex"
          title="Google drive sync"
          style={{ border: '1px solid var(--bq-border-muted)' }}
          pt={{
            content: { className: 'p-0' },
            body: { className: 'w-full' },
          }}
        >
          <MessageComponent msgText="To access folder from google drive, please share them with Banqora's service account: " />
          <div className="h-25rem mt-3">
            <FundsTable
              rowData={fundList.map((fund) => ({
                ...fund,
                jurisdictionName: fund.jurisdiction.name,
                inceptionDate: new Date(fund.inceptionDate),
                terminationDate: new Date(fund.inceptionDate),
                handleAddFolderSync: (fundId: string) => {
                  setGDriveFolderSyncVisible(true);
                  setSelectedFundId(fundId);
                  setIsPcFolderMapped(fund.pcGDriveFolderSync);
                },
                googleWatchChannels:
                  fund.googleWatchChannels &&
                  fund.googleWatchChannels.map(
                    (googleWatchChannel: GoogleWatchChannel) => ({
                      ...googleWatchChannel,
                      handleRemoveSync: (channelId: string) =>
                        removeGoogleWatchChannelSync(channelId),
                    }),
                  ),
              }))}
            />
          </div>
        </Card>
        {orgRole === 'org:admin' && (
          <Card
            className="mt-4"
            style={{ border: '1px solid var(--bq-border-muted)' }}
            title="Invite your team"
          >
            <div className="flex flex-column">
              <div className="flex justify-content-end">
                <Button
                  label="Invite members"
                  icon="pi pi-send"
                  onClick={handleSendInvite}
                />
              </div>
              <div className="flex">
                <TabView className="w-full">
                  <TabPanel
                    header={`Members (${memberships?.data?.length || 0})`}
                  >
                    <div className="h-20rem">
                      <MembersTable
                        loading={!isLoaded}
                        rowData={
                          memberships?.data?.map((data) => ({
                            email: data.publicUserData.identifier,
                            fullName: getFullName(data),
                            imageUrl: data.publicUserData.imageUrl,
                            role: data.role.split(':')[1],
                            joined: data.createdAt,
                          })) || []
                        }
                      />
                    </div>
                  </TabPanel>
                  <TabPanel
                    header={`Invites (${invitations?.data?.length || 0})`}
                  >
                    <div className="h-20rem">
                      <InvitesTable
                        loading={!isLoaded}
                        rowData={
                          invitations?.data?.map((data) => ({
                            email: data.emailAddress,
                            status: data.status,
                            role: data.role.split(':')[1],
                            invited: data.createdAt,
                            handleRevoke: () =>
                              showRevokeInviteDialog(data.emailAddress),
                          })) || []
                        }
                      />
                    </div>
                  </TabPanel>
                </TabView>
              </div>
            </div>
          </Card>
        )}
        <Dialog
          header="Invite new members"
          className="pb-0"
          visible={inviteDialogVisible}
          style={{ width: '50vw' }}
          onHide={() => closeInviteDialog()}
        >
          <form onSubmit={handleInvitesSubmit(onInvitesFormSubmit)}>
            <div className="flex flex-column gap-2">
              <label htmlFor="emails">
                Enter emails to invite members to your organization
              </label>
              <Controller
                name="emails"
                control={control}
                rules={{ validate: validateEmails }}
                render={({ field }) => (
                  <Chips
                    invalid={!!errors.emails}
                    id="emails"
                    separator=","
                    addOnBlur
                    allowDuplicate={false}
                    onBlur={() => trigger('emails')}
                    className="p-fluid flex"
                    value={field.value}
                    onChange={(e: ChipsChangeEvent) => {
                      setFieldValue('emails', e.value || []);
                      trigger('emails');
                    }}
                  />
                )}
              />
              {errors.emails && (
                <small className="p-error">{errors.emails.message}</small>
              )}
              <label htmlFor="role">Select a role</label>
              <Controller
                name="role"
                control={control}
                rules={{ required: 'Role is required.' }}
                render={({ field }) => (
                  <Dropdown
                    pt={{
                      panel: {
                        style: { zIndex: 999999 },
                      },
                    }}
                    id="role"
                    invalid={!!errors.role}
                    value={field.value}
                    options={availableRoles}
                    onChange={(e) => {
                      setFieldValue('role', e.value);
                      trigger('role');
                    }}
                    placeholder="Select a role"
                  />
                )}
              />
              {errors.role && (
                <small className="p-error">{errors.role.message}</small>
              )}

              <div className="flex justify-content-end">
                <Button
                  className="mt-2"
                  type="submit"
                  label="Send"
                  icon="pi pi-envelope"
                  loading={inviteUserLoading}
                  disabled={!!errors.emails}
                />
              </div>
            </div>
          </form>
        </Dialog>
        <Dialog
          header="Select folders for Auto Sync"
          className="pb-0"
          visible={gDriveFolderSyncVisible}
          style={{ width: '50vw' }}
          onHide={() => setGDriveFolderSyncVisible(false)}
        >
          <GoogleDriveUploader
            onSubmitComplete={() => {
              fetchedFundsList();
              setGDriveFolderSyncVisible(false);
            }}
            isGDriveSyncMode
            fundId={selectedFundId}
            showPcFolderMapping
            isPcFolderMapped={isPcFolderMapped}
            refetchFund={() => fetchedFundsList()}
          />
        </Dialog>
        <ConfirmDialog />
      </PageContent>
    </BasicPage>
  );
}
